import {KeyCode, safeText} from "../../js/util/CommonUtils.js";

export default class DropdownInput extends HTMLElement {
    /**
     * @type {TextInput}
     */
    #inputElement;
    /** @type {DropdownList} */
    #dropdownElement;
    #currentValue;
    #callbacks = {
        onChangeValue: [],
        onOptionSelected: [],
        onClearInput: [],
    }

    connectedCallback() {
        this.innerHTML = this.#htmlTemplate();
        this.#inputElement = this.querySelector("text-input");
        this.#dropdownElement = this.querySelector("dropdown-list");
        this.#addListeners();
    }

    get value() {
        return this.#currentValue;
    }

    get rawValue() {
        return this.#inputElement.value;
    }

    /** @param {DropdownInputElement} value */
    set value(value) {
        this.#currentValue = value;
        this.#inputElement.value = value && value.inputValue ? value.inputValue : "";
    }

    /** @param {DropdownInputElement[]} elementObjects */
    set elements(elementObjects) {
        this.#dropdownElement.options = elementObjects;
    }

    /**
     * @param {DropdownInputElement} value
     * Sets the initial value of the input, as well as the last valid value.
     */
    set initialValue(value) {
        this.value = value;
    }

    /** @param {string} message */
    set errorMessage(message) {
        this.#inputElement.errorMessage = message;
    }

    /** @returns {string} */
    get errorMessage(){
        return this.#inputElement.errorMessage
    }

    /** @returns {boolean} */
    checkValidity() {
        return this.#validate();
    }

    /** @param {function} cb */
    onChangeValue(cb) {
        this.#callbacks.onChangeValue.push(cb);
    }

    /** @param {function} cb */
    onOptionSelected(cb){
        this.#callbacks.onOptionSelected.push(cb);
    }

    /** @param {function} cb */
    onClearInput(cb){
        this.#callbacks.onClearInput.push(cb);
    }

    /** @param {boolean} isDisabled */
    setDisabled(isDisabled) {
        this.toggleAttribute("disabled", isDisabled);
        this.#inputElement.setDisabled(isDisabled);
    }

    clearInput(){
        this.#inputElement.value = "";
        this.#dropdownElement.options = [];
        this.#currentValue = null;
    }

    #onInput() {
        this.errorMessage = "";
        this.#currentValue = null;
        if (!this.#dropdownElement.isVisible())
            this.#dropdownElement.show();
    }

    #onFocus() {
        this.#dropdownElement.show();
    }

    #onBlur() {
        this.#hideDropdownAndNotify();
    }

    /** @param {DropdownInputElement} option */
    #onOptionClick(option) {
        this.value = option;
        this.#hideDropdownAndNotify();
        this.#callbacks.onOptionSelected.forEach(callback => callback(this.value, this.#inputElement.rawValue));
    }

    #onClearIconClick() {
        this.clearInput();
        this.#callbacks.onClearInput.forEach(callback => callback())
        this.#inputElement.querySelector('input').focus();
    }

    #validate() {
        if (this.#inputElement.hasAttribute("required") && !this.value) {
            this.#inputElement.errorMessage = "Required";
            return false
        }
        this.#inputElement.errorMessage = "";
        return true
    }

    #onKeydown(event) {
        if ([KeyCode.Up, KeyCode.Down].includes(event.code)) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
        switch (event.key) {
            case KeyCode.Esc: {
                this.#hideDropdownAndNotify();
                break;
            }
            case KeyCode.Enter: {
                if (this.#dropdownElement.isVisible() && this.#dropdownElement.options.length) {
                    event.stopPropagation();
                    if (this.#dropdownElement.isElementDisabled())
                        break
                    if (this.#dropdownElement.hasSelectedElement())
                        this.#dropdownElement.triggerClickOnSelectedItem();
                    else
                        this.#hideDropdownAndNotify();
                }
                break;
            }
            case KeyCode.Down: {
                if (!this.#dropdownElement.isVisible())
                    this.#dropdownElement.show();
                this.#dropdownElement.selectNextItem();
                break;
            }
            case KeyCode.Up: {
                if (!this.#dropdownElement.isVisible())
                    this.#dropdownElement.show();
                this.#dropdownElement.selectPrevItem();
                break;
            }
        }
    }

    #onInputValueChange() {
        this.#notify();
    }

    #hideDropdownAndNotify() {
        this.#dropdownElement.hide();
        this.#notify();
    }

    #notify() {
        this.#callbacks.onChangeValue.forEach(callback => callback(this.value, this.#inputElement.rawValue));
    }

    #addListeners() {
        const input = this.#inputElement.querySelector("input");
        this.addEventListener("keydown", this.#onKeydown.bind(this));
        this.addEventListener("keyup", (event)=>{event.stopPropagation()});
        input.addEventListener("input", this.#onInput.bind(this));
        input.addEventListener("blur", this.#onBlur.bind(this));
        input.addEventListener('focus', this.#onFocus.bind(this));
        this.#inputElement.onTrailingIconClick(this.#onClearIconClick.bind(this));
        this.#inputElement.onChangeValue(this.#onInputValueChange.bind(this));
        this.#dropdownElement.onOptionClick(this.#onOptionClick.bind(this));
    }

    #htmlTemplate() {
        const requiredAttribute = this.hasAttribute("required") ? "required" : "";
        const disabledAttribute = this.hasAttribute("disabled") ? "disabled" : "";
        const iconName = this.hasAttribute("no-icon") ? "" : "close";
        const placeholderAttribute = this.hasAttribute('placeholder') ? `placeholder="${safeText(this.getAttribute('placeholder'))}"` : ""
        return `
            <text-input 
                class="text-input--with-right-icon" 
                label="${this.getAttribute("label")}" 
                ${requiredAttribute}
                ${disabledAttribute}
                ${placeholderAttribute}
                data-icon="${iconName}"
                autocomplete="off"
            ></text-input>
            <dropdown-list></dropdown-list>`;
    }

    static get is() {
        return 'dropdown-input';
    }
}

